import { ToastService } from '@presentation/components/Toast';

export interface HybridRecoveryKeyOnceOptions {
  /** Clé déjà formatée (14 groupes de 4, séparés par des tirets). */
  recoveryKey: string;
  onConfirmed: () => void | Promise<void>;
  onCancel?: () => void;
}

/**
 * Panneau réutilisable : affichage UNIQUE de la clé de récupération.
 * Ne journalise jamais la clé ; l’hôte doit vider le DOM après confirmation.
 */
export function renderHybridRecoveryKeyOnce(
  host: HTMLElement,
  options: HybridRecoveryKeyOnceOptions,
): void {
  host.replaceChildren();

  const root = document.createElement('div');
  root.className = 'hybrid-recovery-key-once';
  root.setAttribute('data-hybrid-recovery-panel', '');

  const warn = document.createElement('div');
  warn.className = 'banner danger';
  warn.setAttribute('role', 'alert');
  warn.innerHTML = `
    <span class="icon" aria-hidden="true">⚠</span>
    <div>
      <strong>Cette clé ne sera plus jamais affichée.</strong>
      <p style="margin:6px 0 0;font-size:0.9rem;line-height:1.45;">
        Sans cette clé <em>et</em> sans votre phrase secrète, vos sauvegardes chiffrées seront
        <strong>IRRÉCUPÉRABLES</strong>. Imprimez-la ou copiez-la dans un coffre hors ligne,
        puis confirmez ci-dessous.
      </p>
    </div>
  `;

  const keyBlock = document.createElement('div');
  keyBlock.className = 'field';
  keyBlock.style.marginTop = '18px';

  const keyLabel = document.createElement('label');
  keyLabel.htmlFor = 'hybrid-recovery-key-display';
  keyLabel.textContent = 'Clé de récupération (affichage unique)';

  const keyDisplay = document.createElement('pre');
  keyDisplay.id = 'hybrid-recovery-key-display';
  keyDisplay.setAttribute('aria-live', 'polite');
  keyDisplay.style.cssText =
    'margin:0;padding:14px 16px;background:var(--neige,#f5f7f9);border:1px solid #e1e4e8;' +
    'border-radius:6px;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;' +
    'font-size:0.95rem;line-height:1.6;letter-spacing:0.04em;white-space:pre-wrap;word-break:break-all;' +
    'user-select:all;';
  keyDisplay.textContent = options.recoveryKey;

  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;flex-wrap:wrap;gap:10px;margin-top:12px;';

  const printBtn = document.createElement('button');
  printBtn.type = 'button';
  printBtn.className = 'btn btn-secondary';
  printBtn.textContent = 'Imprimer';

  const copyBtn = document.createElement('button');
  copyBtn.type = 'button';
  copyBtn.className = 'btn btn-secondary';
  copyBtn.textContent = 'Copier';

  actions.append(printBtn, copyBtn);

  const checkboxField = document.createElement('div');
  checkboxField.className = 'field';
  checkboxField.style.cssText = 'margin-top:18px;flex-direction:row;align-items:flex-start;gap:10px;';

  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.id = 'hybrid-recovery-key-stored';
  checkbox.setAttribute('aria-describedby', 'hybrid-recovery-key-stored-hint');

  const checkboxLabel = document.createElement('label');
  checkboxLabel.htmlFor = 'hybrid-recovery-key-stored';
  checkboxLabel.style.cssText = 'font-size:0.9rem;font-weight:600;line-height:1.4;cursor:pointer;';
  checkboxLabel.textContent = "Je l'ai conservée en lieu sûr";

  const checkboxHint = document.createElement('span');
  checkboxHint.id = 'hybrid-recovery-key-stored-hint';
  checkboxHint.className = 'hint';
  checkboxHint.style.cssText = 'display:block;margin-top:4px;flex-basis:100%;margin-left:1.75rem;';
  checkboxHint.textContent =
    'Obligatoire pour activer la sauvegarde chiffrée. Cochez uniquement après impression ou copie sécurisée.';

  const checkboxWrap = document.createElement('div');
  checkboxWrap.style.cssText = 'display:flex;flex-direction:column;gap:0;';
  const checkboxRow = document.createElement('div');
  checkboxRow.style.cssText = 'display:flex;align-items:flex-start;gap:10px;';
  checkboxRow.append(checkbox, checkboxLabel);
  checkboxWrap.append(checkboxRow, checkboxHint);
  checkboxField.append(checkboxWrap);

  const errorEl = document.createElement('p');
  errorEl.id = 'hybrid-recovery-key-error';
  errorEl.setAttribute('role', 'alert');
  errorEl.style.cssText = 'display:none;margin:12px 0 0;color:var(--rouge,#b42318);font-size:0.9rem;';

  const footer = document.createElement('div');
  footer.style.cssText = 'display:flex;justify-content:flex-end;gap:10px;margin-top:22px;';

  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.className = 'btn btn-ghost';
  cancelBtn.textContent = 'Annuler';

  const confirmBtn = document.createElement('button');
  confirmBtn.type = 'button';
  confirmBtn.className = 'btn btn-primary';
  confirmBtn.textContent = 'Confirmer';
  confirmBtn.disabled = true;
  confirmBtn.setAttribute('aria-describedby', 'hybrid-recovery-key-stored-hint');

  footer.append(cancelBtn, confirmBtn);

  keyBlock.append(keyLabel, keyDisplay, actions);
  root.append(warn, keyBlock, checkboxField, errorEl, footer);
  host.appendChild(root);

  const showError = (message: string): void => {
    errorEl.textContent = message;
    errorEl.style.display = 'block';
  };
  const clearError = (): void => {
    errorEl.textContent = '';
    errorEl.style.display = 'none';
  };

  const syncConfirmEnabled = (): void => {
    confirmBtn.disabled = !checkbox.checked;
  };

  checkbox.addEventListener('change', () => {
    clearError();
    syncConfirmEnabled();
  });

  printBtn.addEventListener('click', () => {
    // Cadre masqué plutôt qu'une fenêtre : window.open avec « noopener » renvoie toujours null,
    // et aucun script n'est injecté (CSP). Le cadre est retiré juste après l'impression.
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    frame.style.cssText = 'position:fixed;width:0;height:0;border:0;';
    frame.srcdoc =
      `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title>Clé de récupération Nexxus</title>` +
      `<style>body{font-family:system-ui,sans-serif;padding:32px;line-height:1.5;}` +
      `pre{font-family:ui-monospace,Menlo,Consolas,monospace;font-size:16px;letter-spacing:0.06em;` +
      `padding:16px;border:1px solid #ccc;border-radius:6px;white-space:pre-wrap;word-break:break-all;}` +
      `p.warn{color:#b42318;font-weight:600;}</style></head><body>` +
      `<h1>Clé de récupération — sauvegarde chiffrée Nexxus</h1>` +
      `<p class="warn">Conservez ce document hors ligne. Sans cette clé et sans votre phrase secrète, ` +
      `vos sauvegardes seront IRRÉCUPÉRABLES. Cette clé ne sera plus affichée dans l’application.</p>` +
      `<pre>${escapeHtml(options.recoveryKey)}</pre></body></html>`;
    frame.addEventListener('load', () => {
      try {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
      } catch {
        ToastService.error('Impression impossible — utilisez « Copier » et conservez la clé hors ligne.');
      } finally {
        frame.remove();
      }
    });
    document.body.appendChild(frame);
  });

  copyBtn.addEventListener('click', () => {
    void (async () => {
      try {
        await navigator.clipboard.writeText(options.recoveryKey);
        ToastService.success('Clé copiée dans le presse-papiers');
      } catch {
        // Repli : sélection + execCommand (environnements restreints)
        const range = document.createRange();
        range.selectNodeContents(keyDisplay);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        const ok = document.execCommand('copy');
        selection?.removeAllRanges();
        if (ok) {
          ToastService.success('Clé copiée dans le presse-papiers');
        } else {
          ToastService.error('Impossible de copier automatiquement — sélectionnez la clé manuellement.');
        }
      }
    })();
  });

  cancelBtn.addEventListener('click', () => {
    options.onCancel?.();
  });

  confirmBtn.addEventListener('click', () => {
    clearError();
    if (!checkbox.checked) {
      showError("Cochez « Je l'ai conservée en lieu sûr » avant de confirmer.");
      checkbox.focus();
      checkbox.setAttribute('aria-invalid', 'true');
      return;
    }
    checkbox.removeAttribute('aria-invalid');
    confirmBtn.disabled = true;
    void Promise.resolve(options.onConfirmed()).catch(() => {
      confirmBtn.disabled = false;
      syncConfirmEnabled();
    });
  });

  requestAnimationFrame(() => {
    printBtn.focus();
  });
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
